import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import SidebarNavigation from '../components/SidebarNavigation'
import './MainPage.css'

interface Run {
  id: string
  name: string
  created_at: string
}

interface Agent {
  id: string
  name: string
}

interface Group {
  id: string
  name: string
}

interface StatRow {
  group_id: string
  group_name: string
  agent_id: string
  agent_name: string
  total_tasks: number
  pass_count: number
  avg_cost: number
}

interface DashboardData {
  runs: Run[]
  agents: Agent[]
  groups: Group[]
  stats: StatRow[]
  selectedRunId: string | null
}

type TimeframeValue = 'last_24h' | 'last_7d' | 'last_30d' | 'all_time'

const TIMEFRAME_LABELS: Record<TimeframeValue, string> = {
  last_24h: 'Last 24 Hours',
  last_7d: 'Last 7 Days',
  last_30d: 'Last 30 Days',
  all_time: 'All Time',
}

function MainPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeValue>('last_24h')

  // Dropdown open states
  const [runMenuOpen, setRunMenuOpen] = useState(false)
  const [groupMenuOpen, setGroupMenuOpen] = useState(false)
  const [agentMenuOpen, setAgentMenuOpen] = useState(false)
  const [timeframeMenuOpen, setTimeframeMenuOpen] = useState(false)

  const fetchDashboard = useCallback(async (runId?: string | null) => {
    try {
      const params = new URLSearchParams()
      if (runId) params.set('runId', runId)
      params.set('timeframe', selectedTimeframe)
      const res = await fetch(`/api/dashboard?${params.toString()}`)
      const json = await res.json()
      setData(json)
      if (!runId && json.selectedRunId) {
        setSelectedRunId(json.selectedRunId)
      }
    } catch (err) {
      console.error('Failed to fetch dashboard:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedTimeframe])

  useEffect(() => {
    fetchDashboard(selectedRunId)
  }, [fetchDashboard, selectedRunId])

  const handleRunSelect = (runId: string) => {
    setSelectedRunId(runId)
    setRunMenuOpen(false)
  }

  const handleGroupSelect = (groupName: string | null) => {
    setSelectedGroup(groupName)
    setGroupMenuOpen(false)
  }

  const handleAgentSelect = (agentName: string | null) => {
    setSelectedAgent(agentName)
    setAgentMenuOpen(false)
  }

  const handleTimeframeSelect = (tf: TimeframeValue) => {
    setSelectedTimeframe(tf)
    setTimeframeMenuOpen(false)
  }

  // Get current run label
  const currentRun = data?.runs.find((r) => r.id === selectedRunId)
  const runLabel = currentRun ? currentRun.name : 'Select Run'

  // Filter stats by group and agent
  const filteredStats = (data?.stats || []).filter((s) => {
    if (selectedGroup && s.group_name !== selectedGroup) return false
    if (selectedAgent && s.agent_name !== selectedAgent) return false
    return true
  })

  // Group stats by failure group
  const groupedStats = new Map<string, { groupName: string; rows: StatRow[] }>()
  for (const s of filteredStats) {
    if (!groupedStats.has(s.group_id)) {
      groupedStats.set(s.group_id, { groupName: s.group_name, rows: [] })
    }
    groupedStats.get(s.group_id)!.rows.push(s)
  }

  return (
    <div className="main-page">
      <SidebarNavigation />
      <main className="app-page-main">
        <h1>Dashboard</h1>

        <div data-testid="filter-bar" className="filter-bar">
          {/* Run Dropdown */}
          <div className="filter-dropdown">
            <button
              type="button"
              data-testid="filter-run-trigger"
              className="filter-trigger"
              onClick={() => {
                setRunMenuOpen(!runMenuOpen)
                setGroupMenuOpen(false)
                setAgentMenuOpen(false)
                setTimeframeMenuOpen(false)
              }}
            >
              {runLabel}
            </button>
            {runMenuOpen && (
              <div data-testid="filter-run-menu" className="filter-menu">
                {(data?.runs || []).map((run) => (
                  <button
                    key={run.id}
                    type="button"
                    className="filter-menu-option"
                    onClick={() => handleRunSelect(run.id)}
                  >
                    {run.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Failure Group Dropdown */}
          <div className="filter-dropdown">
            <button
              type="button"
              data-testid="filter-group-trigger"
              className="filter-trigger"
              onClick={() => {
                setGroupMenuOpen(!groupMenuOpen)
                setRunMenuOpen(false)
                setAgentMenuOpen(false)
                setTimeframeMenuOpen(false)
              }}
            >
              {selectedGroup || 'Failure Group: All'}
            </button>
            {groupMenuOpen && (
              <div data-testid="filter-group-menu" className="filter-menu">
                <button
                  type="button"
                  data-testid="filter-group-option-__all__"
                  className="filter-menu-option"
                  onClick={() => handleGroupSelect(null)}
                >
                  All
                </button>
                {(data?.groups || []).map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    data-testid={`filter-group-option-${g.name}`}
                    className="filter-menu-option"
                    onClick={() => handleGroupSelect(g.name)}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Agent Dropdown */}
          <div className="filter-dropdown">
            <button
              type="button"
              data-testid="filter-agent-trigger"
              className="filter-trigger"
              onClick={() => {
                setAgentMenuOpen(!agentMenuOpen)
                setRunMenuOpen(false)
                setGroupMenuOpen(false)
                setTimeframeMenuOpen(false)
              }}
            >
              {selectedAgent || 'Agent: All'}
            </button>
            {agentMenuOpen && (
              <div data-testid="filter-agent-menu" className="filter-menu">
                <button
                  type="button"
                  data-testid="filter-agent-option-__all__"
                  className="filter-menu-option"
                  onClick={() => handleAgentSelect(null)}
                >
                  All
                </button>
                {(data?.agents || []).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`filter-agent-option-${a.name}`}
                    className="filter-menu-option"
                    onClick={() => handleAgentSelect(a.name)}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Timeframe Dropdown */}
          <div className="filter-dropdown">
            <button
              type="button"
              data-testid="filter-timeframe-trigger"
              className="filter-trigger"
              onClick={() => {
                setTimeframeMenuOpen(!timeframeMenuOpen)
                setRunMenuOpen(false)
                setGroupMenuOpen(false)
                setAgentMenuOpen(false)
              }}
            >
              {TIMEFRAME_LABELS[selectedTimeframe]}
            </button>
            {timeframeMenuOpen && (
              <div data-testid="filter-timeframe-menu" className="filter-menu">
                {(Object.keys(TIMEFRAME_LABELS) as TimeframeValue[]).map((tf) => (
                  <button
                    key={tf}
                    type="button"
                    data-testid={`filter-timeframe-option-${tf}`}
                    className="filter-menu-option"
                    onClick={() => handleTimeframeSelect(tf)}
                  >
                    {TIMEFRAME_LABELS[tf]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View Full Statistics Button */}
          <button
            type="button"
            data-testid="filter-view-full-stats"
            className="filter-view-full-stats"
            onClick={() => navigate('/runs')}
          >
            View Full Statistics
          </button>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div data-testid="stats-grid" className="stats-grid">
            {Array.from(groupedStats.entries()).map(([groupId, { groupName, rows }]) => (
              <div key={groupId} data-testid="failure-group-stats-card" className="failure-group-stats-card">
                <h3 data-testid="stats-card-title">{groupName}</h3>
                <table className="stats-card-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Success Rate</th>
                      <th>Avg Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const successRate = row.total_tasks > 0
                        ? Math.round((Number(row.pass_count) / Number(row.total_tasks)) * 100)
                        : 0
                      return (
                        <tr key={row.agent_id} data-testid="stats-card-agent-row">
                          <td data-testid="stats-agent-name">{row.agent_name}</td>
                          <td>{successRate}%</td>
                          <td>${Number(row.avg_cost).toFixed(2)}/run</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

export default MainPage
